import type { NatsConnection, Subscription } from 'nats.ws';
import type {
  NatsManagerConfig,
  NatsMessageCallback,
  LoggerConfig,
  AnalyticsConfig,
} from '../types';

/**
 * NATS Event Names for Analytics
 * Matches the event names in nats.tracking.ts for consistency
 */
export const NATS_EVENT_NAMES = {
  CONNECTED: 'indstocks_nats_connected',
  DISCONNECTED: 'indstocks_nats_connection_disconnect',
  RECONNECTED: 'indstocks_nats_connection_reconnect',
  ERROR: 'indstocks_nats_connection_error',
  SUBSCRIPTION_SUCCESS: 'indstocks_nats_subscription_success',
  SUBSCRIPTION_ERROR: 'indstocks_nats_subscription_error',
  UNSUBSCRIPTION_SUCCESS: 'indstocks_nats_unsubscription_success',
  CONNECTION_CLOSED: 'indstocks_nats_closed',
  CONNECTION_STALE: 'indstocks_nats_connection_stale',
  /** Fired when window gains focus and connection is closed */
  CONNECTION_CLOSED_ON_FOCUS: 'indstocks_nats_connection_closed_on_focus',
  /** Fired when window gains focus and connection is draining */
  CONNECTION_DRAINING_ON_FOCUS: 'indstocks_nats_connection_draining_on_focus',
} as const;

// ============================================
// Subscription Info
// ============================================

interface SubscriptionInfo {
  subject: string;
  callback: NatsMessageCallback;
  subId: string;
  natsSubscription?: Subscription;
}

// ============================================
// NatsManager Class
// ============================================

class NatsManager {
  private static instance: NatsManager | null = null;
  private connection: NatsConnection | null = null;
  private config: NatsManagerConfig | null = null;
  private subscriptions: Map<string, SubscriptionInfo[]> = new Map();
  private isConnecting: boolean = false;
  private logger?: LoggerConfig;
  private analytics?: AnalyticsConfig;

  private constructor() {}

  // ============================================
  // Singleton Pattern
  // ============================================

  static getInstance(): NatsManager {
    if (!this.instance) {
      this.instance = new NatsManager();
    }
    return this.instance;
  }

  static resetInstance(): void {
    if (this.instance) {
      void this.instance.disconnect();
    }
    this.instance = null;
  }

  // ============================================
  // Logging & Analytics
  // ============================================

  private log(
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    data?: Record<string, unknown>,
  ): void {
    if (this.logger) {
      this.logger.log(level, message, data);
    } else if (level === 'error') {
      console.error(`[NatsManager] ${message}`, data);
    } else {
      console.log(`[NatsManager] ${message}`, data);
    }
  }

  private trackEvent(eventName: string, data: Record<string, unknown>): void {
    if (this.analytics) {
      this.analytics.trackEvent(eventName, data);
    }
  }

  // ============================================
  // Connection Management
  // ============================================

  async connect(config: NatsManagerConfig): Promise<void> {
    if (this.connection && !this.connection.isClosed()) {
      this.log('debug', 'NATS connection already exists');
      return;
    }

    if (this.isConnecting) {
      this.log('debug', 'NATS connection already in progress');
      return;
    }

    this.isConnecting = true;
    this.config = config;
    this.logger = config.logger;
    this.analytics = config.analytics;

    try {
      const { connect, credsAuthenticator } = await import('nats.ws');

      const nc = await connect({
        servers: [config.server],
        authenticator: credsAuthenticator(
          new TextEncoder().encode(config.token),
        ),
        reconnect: config.reconnect ?? true,
        maxReconnectAttempts: config.maxReconnectAttempts ?? -1,
        waitOnFirstConnect: config.waitOnFirstConnect ?? true,
        reconnectTimeWait: config.reconnectTimeWait ?? 2000,
      });

      this.connection = nc;

      this.log('info', 'NATS connected', {
        server: nc.getServer(),
        status: nc.isClosed() ? 'closed' : 'connected',
      });

      this.trackEvent(NATS_EVENT_NAMES.CONNECTED, {
        server: nc.getServer(),
        status: nc.isClosed() ? 'closed' : 'connected',
      });

      // Set up status monitoring
      void this.monitorConnectionStatus(nc);

      // Set up closed handler
      nc.closed()
        .then(() => {
          this.log('info', 'NATS connection closed');
          this.trackEvent(NATS_EVENT_NAMES.CONNECTION_CLOSED, {
            server: config.server,
          });
        })
        .catch((err) => {
          this.log('error', 'Error in closing NATS connection', { error: err });
        });
    } catch (error: unknown) {
      this.log('error', 'Failed to connect to NATS', {
        error: error instanceof Error ? error.message : String(error),
      });
      this.trackEvent(NATS_EVENT_NAMES.ERROR, {
        error: error instanceof Error ? error.message : String(error),
        server: config.server,
      });
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  private async monitorConnectionStatus(nc: NatsConnection): Promise<void> {
    try {
      const { Events, DebugEvents } = await import('nats.ws');

      for await (const status of nc.status()) {
        this.log('debug', `NATS status: ${status.type}`, {
          data:
            typeof status.data === 'string'
              ? status.data
              : JSON.stringify(status.data),
        });

        if (status.type === Events.Disconnect) {
          this.trackEvent(NATS_EVENT_NAMES.DISCONNECTED, {
            status: nc.isClosed() ? 'closed' : 'connected',
            eventType: status.type,
            data: JSON.stringify(status.data),
          });
        } else if (status.type === Events.Reconnect) {
          this.trackEvent(NATS_EVENT_NAMES.RECONNECTED, {
            status: nc.isClosed() ? 'closed' : 'connected',
            eventType: status.type,
            data: JSON.stringify(status.data),
          });
        } else if (status.type === Events.Error) {
          this.trackEvent(NATS_EVENT_NAMES.ERROR, {
            status: nc.isClosed() ? 'closed' : 'connected',
            eventType: status.type,
            data: JSON.stringify(status.data),
          });
        } else if (status.type === DebugEvents.StaleConnection) {
          this.trackEvent(NATS_EVENT_NAMES.CONNECTION_STALE, {
            status: nc.isClosed() ? 'closed' : 'connected',
            eventType: status.type,
            data: JSON.stringify(status.data),
          });
        }
      }
    } catch (error: unknown) {
      this.log('error', 'Error monitoring NATS status', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) {
      return;
    }

    try {
      // Unsubscribe all subscriptions
      this.subscriptions.forEach((subs, subject) => {
        subs.forEach((sub) => {
          if (sub.natsSubscription) {
            sub.natsSubscription.unsubscribe();
          }
        });
      });
      this.subscriptions.clear();

      if (!this.connection.isClosed()) {
        await this.connection.drain();
      }
    } catch (error: unknown) {
      this.log('error', 'Error disconnecting from NATS', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      this.connection = null;
    }
  }

  // ============================================
  // Connection State
  // ============================================

  getConnection(): NatsConnection | null {
    return this.connection;
  }

  isConnected(): boolean {
    return this.connection !== null && !this.connection.isClosed();
  }

  isDraining(): boolean {
    return this.connection?.isDraining() ?? false;
  }

  getConnectionStatus(): 'connected' | 'draining' | 'closed' {
    if (!this.connection) {
      return 'closed';
    }
    if (this.connection.isDraining()) {
      return 'draining';
    }
    return this.connection.isClosed() ? 'closed' : 'connected';
  }

  /**
   * Get the current NATS server URL
   */
  getServer(): string | undefined {
    return this.connection?.getServer();
  }

  /**
   * Check connection health - useful for window focus handlers and periodic health checks
   * @returns status and whether reconnection is needed
   */
  checkConnectionHealth(): {
    status: 'connected' | 'draining' | 'closed';
    needsReconnect: boolean;
  } {
    const status = this.getConnectionStatus();
    const needsReconnect = status === 'closed';

    this.log('debug', 'Connection health check', { status, needsReconnect });

    return { status, needsReconnect };
  }

  /**
   * Track connection status on window focus - fires appropriate analytics events
   * @param timeSinceLastFocus Time in ms since the last focus event
   * @param reOpenConnectionCount Number of times connection has been reopened
   */
  trackConnectionStatusOnFocus(
    timeSinceLastFocus: number,
    reOpenConnectionCount: number = 0,
  ): void {
    const status = this.getConnectionStatus();

    if (status === 'closed') {
      this.trackEvent(NATS_EVENT_NAMES.CONNECTION_CLOSED_ON_FOCUS, {
        status,
        reOpenConnectionCount,
        timeSinceLastFocus,
      });
    } else if (status === 'draining') {
      this.trackEvent(NATS_EVENT_NAMES.CONNECTION_DRAINING_ON_FOCUS, {
        status,
        reOpenConnectionCount,
        timeSinceLastFocus,
      });
    }
  }

  // ============================================
  // Subscription Management
  // ============================================

  subscribe(subject: string, callback: NatsMessageCallback): () => void {
    if (!this.connection) {
      this.log('error', 'Cannot subscribe: NATS connection not initialized');
      return () => {};
    }

    const subId = `sub-${subject}-${Math.random().toString(8).slice(2, 11)}`;

    try {
      const decoder = new TextDecoder();

      const natsSubscription = this.connection.subscribe(subject, {
        callback: (err, msg) => {
          if (err) {
            callback(null, err);
            return;
          }

          try {
            const decodedData = JSON.parse(decoder.decode(msg.data));
            callback(decodedData);
          } catch (parseError: unknown) {
            callback(
              null,
              parseError instanceof Error
                ? parseError
                : new Error(String(parseError)),
            );
          }
        },
      });

      const subscriptionInfo: SubscriptionInfo = {
        subject,
        callback,
        subId,
        natsSubscription,
      };

      const existingSubs = this.subscriptions.get(subject) ?? [];
      this.subscriptions.set(subject, [...existingSubs, subscriptionInfo]);

      this.log('debug', 'Subscribed to NATS subject', { subject, subId });

      this.trackEvent(NATS_EVENT_NAMES.SUBSCRIPTION_SUCCESS, {
        subject,
        subId,
      });

      // Return unsubscribe function
      return () => {
        this.unsubscribe(subject, subId);
      };
    } catch (error: unknown) {
      this.log('error', 'Error subscribing to NATS subject', {
        subject,
        error: error instanceof Error ? error.message : String(error),
      });

      this.trackEvent(NATS_EVENT_NAMES.SUBSCRIPTION_ERROR, {
        subject,
        error: error instanceof Error ? error.message : String(error),
      });

      return () => {};
    }
  }

  private unsubscribe(subject: string, subId: string): void {
    const subs = this.subscriptions.get(subject);

    if (!subs) {
      return;
    }

    const subToRemove = subs.find((s) => s.subId === subId);
    const filteredSubs = subs.filter((s) => s.subId !== subId);

    if (subToRemove?.natsSubscription) {
      subToRemove.natsSubscription.unsubscribe();
    }

    if (filteredSubs.length === 0) {
      this.subscriptions.delete(subject);
    } else {
      this.subscriptions.set(subject, filteredSubs);
    }

    this.log('debug', 'Unsubscribed from NATS subject', { subject, subId });

    this.trackEvent(NATS_EVENT_NAMES.UNSUBSCRIPTION_SUCCESS, {
      subject,
      subId,
    });
  }

  isSubscribed(subject: string): boolean {
    return this.subscriptions.has(subject);
  }
}

export default NatsManager;
